/**
 * Default pricing copy deck.
 *
 * Every line on the pricing page that is NOT a catalog number lives
 * here, keyed by either a feature slug (`blocking`, `white_label`),
 * a dimension slug (`page_loads_monthly`, `retention_days`), or a
 * plan slug (`free`, `protect`, `business`, `agency-10`, …). The
 * marketing app passes its own deck in; the fallbacks below keep the
 * ops admin preview and any catalog edge rendering sane.
 *
 * No internal language (ADR, catalog, v2, slice, scope, …). The
 * `isInternalDescription` guard scrubs seeded internal descriptions
 * so ops staff who leave the seed text in place still get the
 * marketing-site fallback.
 *
 * Numbers are never baked in here — the page composes them from the
 * live catalog. If a slug isn't known, the renderer falls back to a
 * neutral label built from the dimension/feature slug itself.
 */
/** Case-insensitive match for internal seed language. A description
 *  that mentions "ADR-…", "Plans v2", "catalog", "scope", "S0..S9",
 *  "slice", "template" or "staff" is treated as internal and swapped
 *  for the copy-deck fallback for that plan slug. */
export function isInternalDescription(description) {
    if (description === null || description === undefined)
        return true;
    const s = description.trim();
    if (s.length === 0)
        return true;
    return /ADR-|Plans v2|catalog|slice|S[0-9]\b|scope|template|staff/i.test(s);
}
/** Resolve the plan's pitch — the catalog description wins unless it
 *  reads like internal seed text, in which case the copy deck's slug
 *  fallback takes over. Returns `null` if neither source has a line. */
export function resolvePitch(slug, description, deck) {
    if (!isInternalDescription(description))
        return description;
    const fallback = deck.plans[slug];
    if (fallback !== undefined && fallback.pitch !== undefined && fallback.pitch.length > 0) {
        return fallback.pitch;
    }
    return null;
}
export function resolveAgencyPitch(slug, description, deck) {
    if (!isInternalDescription(description))
        return description;
    const fallback = deck.agency_plans[slug];
    if (fallback !== undefined && fallback.pitch !== undefined && fallback.pitch.length > 0) {
        return fallback.pitch;
    }
    return null;
}
/** Neutral slug-to-label fallback when nothing in the deck matches. */
export function labelFromSlug(slug) {
    const s = slug.replace(/_/g, ' ').replace(/-/g, ' ');
    return s.charAt(0).toUpperCase() + s.slice(1);
}
/** The default copy deck used by the ops admin preview and as a base
 *  the marketing site extends. Deliberately short — the marketing
 *  deck replaces every field. */
export const DEFAULT_COPY_DECK = {
    headline: 'Plans priced per site.',
    subline: 'Every account starts free. Pay per site when you want blocking or more room.',
    scanline: 'The agent readiness scan is free for everyone.',
    scanlineHref: '/',
    assureLine: 'Over a limit, orders keep flowing and your site keeps working. Nothing is charged without your click.',
    assureSub: 'No online store? There are no orders to count.',
    agencyLink: 'Running Sill for clients? See agency plans.',
    agencyLinkNoTiers: 'Running Sill for clients? Talk to us about agency pricing.',
    ctaFree: 'Start free',
    ctaPaid: 'Get started',
    plans: {},
    agency_plans: {},
    features: {
        blocking: {
            label: 'Address and attack-campaign blocking',
            tooltip: 'Block an address for 24 hours, 7 days or for good. Campaign auto-block blocks the hosting and cloud addresses in an attack campaign for 7 days. Runs in the Sill Cloudflare Worker.',
        },
        custom_attack_paths: {
            label: 'Custom attack paths',
            tooltip: 'Add up to 50 paths of your own, such as an old admin page. Anyone who asks for them gets a plain 404.',
        },
        slack_held_alerts: {
            label: 'Slack integration',
            tooltip: 'Each held order is also posted to your Slack channel, with a link to approve or decline it in Sill.',
        },
        assistant_blocking: {
            label: 'Assistant can create blocks',
            tooltip: 'Your connected Claude or ChatGPT can block an address for 24 hours or 7 days.',
        },
        assistant: {
            label: 'AI assistant',
            tooltip: 'Connect Claude or ChatGPT to your Sill account. It can read agent analytics, threats and held orders, and show you the dashboard link for each held order.',
        },
        white_label: {
            label: 'White-label reports',
            tooltip: 'Readiness reports and report emails carry your logo, colour and a contact link. Signed records always identify Sill.',
        },
        agency_portal: {
            label: 'Agency Portal',
            tooltip: 'Open each client\'s dashboard from your Agency Portal, with no second login.',
        },
    },
    dimensions: {
        page_loads_monthly: {
            label: 'Page loads a month',
            tooltip: 'Pages opened on your site by people, AI agents and bots. Images, scripts and styles never count. Going over never slows or blocks your site.',
        },
        orders_monthly: {
            label: 'Agent orders a month',
            tooltip: 'An order an AI agent places through Sill, counted once when the payment succeeds. Retries of the same order count once. Declined, held, failed, refunded, disputed, cancelled and test orders do not count.',
        },
        assistant_requests_monthly: {
            label: 'AI assistant requests',
            tooltip: 'Each time Claude or ChatGPT, connected to your Sill account, reads or does something in Sill. One question can use several.',
        },
        retention_days: {
            label: 'Reporting history',
            tooltip: 'How far back dashboard reports go. Signed records are kept on every plan and export any time.',
        },
        included_sites: {
            label: 'Client sites included',
            tooltip: 'Client sites you can run on the included site plan for one bill.',
        },
        agency_scans_monthly: {
            label: 'Readiness scans a month',
            tooltip: 'Scan client and prospect sites in one batch from your Agency Portal.',
        },
        free_sites: {
            label: 'Free sites per account',
            tooltip: 'Sites you can run on Free, before any paid add-on or site plan.',
        },
    },
    compareSections: [
        {
            kicker: 'Limits',
            rows: ['page_loads_monthly', 'orders_monthly', 'assistant_requests_monthly', 'retention_days'],
        },
    ],
    installMatrix: {
        kicker: 'What each install does',
        heading: 'Every install sees agents. Blocking needs Cloudflare.',
        note: 'Every install sees visitors that run JavaScript, serves your agent card from Sill and checks agent orders. The differences are below.',
        columns: [
            'Agents that skip JavaScript',
            'Attack paths answered with a 404',
            'Block addresses and campaigns',
        ],
        rows: [
            { name: 'Cloudflare Worker', tip: 'Any site whose traffic goes through Cloudflare.', cells: [{ ok: true }, { ok: true }, { ok: true }] },
            { name: 'WordPress plugin', tip: 'WordPress and WooCommerce sites.', cells: [{ ok: true, note: 'Not pages served from a cache' }, { ok: true, note: 'Signed-in users pass' }, { ok: false, note: 'Add the Worker to block' }] },
            { name: 'Shopify app', tip: 'Shopify stores.', cells: [{ ok: false }, { ok: false }, { ok: false }] },
            { name: 'Script tag', tip: 'Any other site.', cells: [{ ok: false }, { ok: false }, { ok: false }] },
        ],
    },
    standards: {
        kicker: 'Why you can trust the rules',
        heading: 'Built on the standards your security team already reads.',
        items: [
            {
                title: 'Rules mapped to open frameworks',
                body: 'Each guardrail rule is mapped to the risks it helps address. These are mappings, not certifications.',
                tags: ['OWASP Agentic Top 10', 'OWASP LLM Top 10', 'MITRE ATLAS', 'NIST AI RMF'],
            },
            {
                title: 'Attack paths answer probes with a 404',
                body: 'Scanners probe sites for files like .env or old config backups. With attack paths on, your Cloudflare Worker or WordPress plugin answers them with a plain 404.',
            },
            {
                title: 'Signed records anyone can check',
                body: 'Export your records on any plan and verify them yourself with Sill\'s public key. No Sill software needed.',
            },
        ],
    },
    finePrint: [
        'Prices are per site, in US dollars. Paying yearly gives two months free on paid plans.',
        'Our Data Processing Addendum covers every plan, including Free.',
    ],
    agencyBand: {
        kicker: 'For agencies',
        heading: 'Run Sill for all your clients.',
        lede: 'Each client keeps their own Sill account, and every client site in your bundle gets the included site plan.',
        features: [
            { label: 'Every client dashboard from one login', tooltip: 'Open each client\'s dashboard from your Agency Portal, with no second login. Clients can revoke your access at any time.' },
            { label: 'Scan client and prospect sites in one batch', tooltip: 'Paste up to 50 domains. Each gets a readiness report you can link to or email. Monthly scans depend on your plan.' },
            { label: 'Your logo on reports and report emails', tooltip: 'Reports carry your logo, colour and a contact link. Signed records always identify Sill.' },
            { label: 'A scan box for your own website', tooltip: 'Visitors check their own site from your website, and each scan lands in your leads list with a report.' },
            { label: 'A referral link that tracks signups', tooltip: 'See clicks and every new account that signs up through your link, in your Agency Portal.' },
        ],
        talkTitle: 'Agency pricing',
        talkMeta: 'Protect on every client site at a lower price per site, and more scans each month.',
        talkCta: 'Talk to us',
        talkHeading: 'Running Sill for clients?',
        talkSubHeading: 'Ten or more client sites',
    },
};
//# sourceMappingURL=copy-deck.js.map